<template>
  <YapModal>
    <template #trigger="{ openModal }">
      <YapButtonIcon
        @click="
          () => {
            loadSchema();
            openModal();
          }
        "
      >
        <YapIcon name="pencil-outline" class="h-5 w-5 text-black dark:text-primary-dark-100" />
      </YapButtonIcon>
    </template>
    <template #title>{{ t('app.payments.attachments_rename_title_v2') }}</template>
    <template #default="{ closeModal }">
      <div class="mt-2 flex w-full gap-2">
        <YapInput
          v-if="state.schema"
          name="displayName"
          :label="t('app.payments.attachments_rename_hint_v2')"
          v-model="state.newName"
          :schema="state.schema.properties.fileName"
          floating
          class="w-full"
        />
        <div class="flex w-fit items-center">.{{ state.fileExtension }}</div>
      </div>
      <div class="mt-4 flex gap-2">
        <YapButton
          :button-size="ButtonSize.Small"
          :button-style="ButtonStyle.Info"
          outlined
          class="w-fit"
          @click="closeModal"
        >
          {{ t('dict.label.cancel') }}
        </YapButton>
        <YapButton
          :button-size="ButtonSize.Small"
          :button-style="ButtonStyle.Info"
          class="w-fit"
          :loading="state.isSaving"
          @click="updateReceipt(closeModal)"
        >
          {{ t('app.payments.attachments_rename_button_confirm_v2') }}
        </YapButton>
      </div>
    </template>
  </YapModal>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';

import apiGw from '@/services/apiGw';
import { useI18n } from '@/plugins/i18n';
import YapIcon from '@/components/YapIcon.vue';
import YapModal from '@/components/YapModal.vue';
import { Method, Schema } from '@/types/general';
import { FileResource } from '@/types/fileResource';
import YapInput from '@/components/form/YapInput.vue';
import { useSchemasStore } from '@/store/schemaStore';
import YapButtonIcon from '@/components/YapButtonIcon.vue';
import YapButton, { ButtonSize, ButtonStyle } from '@/components/YapButton.vue';

const props = defineProps<{
  fileResource: FileResource;
}>();

const emit = defineEmits<{
  update: [{ resourceUid: string; newName: string }];
}>();

const { t } = useI18n();
const schemaStore = useSchemasStore();

interface State {
  isSaving: boolean;
  fileExtension: string;
  newName: string;
  schema?: Schema;
}
const state: State = reactive({
  isSaving: false,
  fileExtension: computed(() => props.fileResource.displayName.split('.').pop() ?? ''),
  newName: props.fileResource.displayName.split('.')[0] ?? '',
  schema: undefined
});

async function loadSchema() {
  state.schema = await schemaStore.getSchema({ path: `/v2/file-resources/1` }, Method.patch);
}

async function updateReceipt(closeModal: () => void) {
  state.isSaving = true;
  const rUid = props.fileResource.resourceUid ?? props.fileResource.uid;
  if (rUid) {
    try {
      await apiGw.patch(`/v2/file-resources/${rUid}`, { fileName: `${state.newName}.${state.fileExtension}` });
      emit('update', { resourceUid: rUid, newName: `${state.newName}.${state.fileExtension}` });
    } catch (error) {
      console.error(`Error renaming file resource: `, error);
    }
  }
  state.isSaving = false;
  closeModal();
}
</script>
